import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Recordings = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const fetchRecordings = useCallback(async () => {
    try {
      const res = await api.get('/api/recordings');
      setRecordings(res.data || []);
    } catch {
      setError('Failed to load recordings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecordings(); }, [fetchRecordings]);

  const filtered = recordings.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.title || '').toLowerCase().includes(q) ||
      (r.class_title || '').toLowerCase().includes(q) ||
      (r.batch_name || '').toLowerCase().includes(q) ||
      (r.uploader_name || '').toLowerCase().includes(q)
    );
  });

  // Group by batch
  const grouped = {};
  filtered.forEach((r) => {
    const key = r.batch_name || 'General';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="skeleton h-8 w-48" />
          <div className="bg-white rounded-2xl border border-gray-200">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between px-6 py-4 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-4 flex-1">
                  <div className="skeleton w-9 h-9 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="skeleton h-4 w-48" />
                    <div className="skeleton h-3 w-64" />
                  </div>
                </div>
                <div className="skeleton h-9 w-36 rounded-xl" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">Retry</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Recordings</h1>
            <p className="text-sm text-gray-500">{recordings.length} recordings available</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, batch or trainer..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
          />
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-14 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No recordings available yet</p>
              <p className="text-xs text-gray-400 mt-1">
                {search ? 'Try a different search term' : 'Recordings will appear here after your trainer adds them'}
              </p>
            </div>
          </div>
        ) : (
          Object.entries(grouped).map(([batchName, items]) => (
            <div key={batchName}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{batchName}</h2>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
                {items.map((rec) => (
                  <div key={rec.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors">
                    {/* Play icon */}
                    <div className="w-9 h-9 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>

                    {/* Info block */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{rec.title || 'Recording'}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                        {rec.class_title && (
                          <span className="text-xs text-gray-500 truncate">{rec.class_title}</span>
                        )}
                        {rec.uploader_name && (
                          <span className="text-xs text-gray-400">by {rec.uploader_name}</span>
                        )}
                        <span className="text-xs text-gray-400">{formatDate(rec.created_at)}</span>
                      </div>
                    </div>

                    {/* Watch button */}
                    <a
                      href={rec.youtube_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 bg-secondary text-white text-sm font-medium rounded-xl hover:bg-secondary/90 transition-colors flex-shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      Watch
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </Layout>
  );
};

export default Recordings;
